import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HardHat, Plus, Upload } from "lucide-react";
import type { WorkerFormOptions, WorkerSummary } from "@crewledger/shared/schemas";
import { EmptyState, ListTableSkeleton, PageHeader, PaginationBar } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { BETA_EMPTY_STATES } from "@/lib/beta-empty-states";
import { fetchWorkerOptions, fetchWorkers } from "./api";
import { WorkerStatusBadge } from "./components/worker-status-badge";
import { useWorkerPermissions } from "./use-worker-permissions";

const PAGE_SIZE = 50;

export function WorkersListPage() {
  const navigate = useNavigate();
  const { canWrite, canViewPayroll } = useWorkerPermissions();
  const [workers, setWorkers] = useState<WorkerSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState<WorkerFormOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [siteId, setSiteId] = useState("");
  const [crew, setCrew] = useState("");
  const [trade, setTrade] = useState("");
  const [status, setStatus] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);

  useEffect(() => {
    fetchWorkerOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() => { setOptions(null); });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, siteId, crew, trade, status]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchWorkers({
      page,
      pageSize: PAGE_SIZE,
      q: debouncedQ || undefined,
      siteId: siteId || undefined,
      crew: crew || undefined,
      trade: trade || undefined,
      status: status || undefined,
    })
      .then((res) => {
        setWorkers(res.data);
        setTotal(res.meta.total ?? res.data.length);
      })
      .catch((err: unknown) =>
        { setError(err instanceof Error ? err.message : "Failed to load workers"); },
      )
      .finally(() => { setLoading(false); });
  }, [page, debouncedQ, siteId, crew, trade, status]);

  const hasActiveFilters = Boolean(debouncedQ || siteId || crew || trade || status);
  const showBetaEmpty = workers.length === 0 && !hasActiveFilters;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Workers"
        description="Manage crew roster, rates, and site assignments."
        actions={
          canWrite ? (
            <div className="flex flex-wrap gap-2">
              <Link to="/workers/import">
                <Button variant="outline">
                  <Upload className="mr-1.5 h-4 w-4" />
                  Import CSV
                </Button>
              </Link>
              <Link to="/workers/new">
                <Button>
                  <Plus className="mr-1.5 h-4 w-4" />
                  New worker
                </Button>
              </Link>
            </div>
          ) : undefined
        }
      />

      {showBetaEmpty ? null : (
      <div className="grid gap-3 rounded-xl border bg-card-white p-4 sm:grid-cols-2 lg:grid-cols-5">
        <Input placeholder="Search ID or name…" value={q} onChange={(e) => { setQ(e.target.value); }} />
        <select
          value={siteId}
          onChange={(e) => { setSiteId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All sites</option>
          {options?.sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} — {s.name}
            </option>
          ))}
        </select>
        <select
          value={crew}
          onChange={(e) => { setCrew(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All crews</option>
          {options?.crews.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={trade}
          onChange={(e) => { setTrade(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All trades</option>
          {options?.trades.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>
      )}

      {error ? (
        <EmptyState icon={HardHat} title="Could not load workers" description={error} />
      ) : loading ? (
        <ListTableSkeleton rows={8} cols={10} />
      ) : workers.length === 0 ? (
        <EmptyState
          icon={HardHat}
          title={showBetaEmpty ? BETA_EMPTY_STATES.workers.title : "No matching workers"}
          description={
            showBetaEmpty
              ? BETA_EMPTY_STATES.workers.description
              : "Try adjusting your search or filters to find workers on your roster."
          }
          actionLabel={showBetaEmpty && canWrite ? BETA_EMPTY_STATES.workers.actionLabel : undefined}
          onAction={showBetaEmpty && canWrite ? () => { navigate("/workers/new"); } : undefined}
          secondaryActionLabel={
            showBetaEmpty && canWrite ? BETA_EMPTY_STATES.workers.secondaryActionLabel : undefined
          }
          onSecondaryAction={
            showBetaEmpty && canWrite ? () => { navigate("/workers/import"); } : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b bg-bg-light text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Employee ID</th>
                  <th className="px-4 py-3">Display name</th>
                  <th className="px-4 py-3">Trade</th>
                  <th className="px-4 py-3">Crew</th>
                  <th className="px-4 py-3">Site</th>
                  <th className="px-4 py-3">Base rate</th>
                  <th className="px-4 py-3">Rule profile</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Period hrs</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workers.map((w) => (
                  <tr key={w.id} className="border-b border-border/60 last:border-0 hover:bg-bg-light/50">
                    <td className="px-4 py-3 font-mono font-medium text-navy-deep">{w.employeeId}</td>
                    <td className="px-4 py-3">{w.displayName}</td>
                    <td className="px-4 py-3 text-muted-foreground">{w.trade ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{w.crew ?? "—"}</td>
                    <td className="px-4 py-3">{w.siteCode ?? "—"}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {canViewPayroll && w.baseHourlyRate != null
                        ? formatCurrency(w.baseHourlyRate)
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{w.ruleProfileName ?? "—"}</td>
                    <td className="px-4 py-3">
                      <WorkerStatusBadge status={w.status} />
                    </td>
                    <td className="px-4 py-3 tabular-nums">{formatHours(w.currentPeriodHours)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/workers/${w.id}`} className="font-medium text-orange-construction hover:underline">
                        View
                      </Link>
                      {canWrite ? (
                        <>
                          {" · "}
                          <Link to={`/workers/${w.id}/edit`} className="text-muted-foreground hover:underline">
                            Edit
                          </Link>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
